import React, {useCallback, useEffect, useState} from 'react';
import styled from 'styled-components';
import {useFormik} from 'formik';
import {useAuth} from '../../../context/AuthContext';
import server from '../../../global/server';
import Typography from '@mui/material/Typography';
import {useApp} from '../../../context/AppContext';
import LoadingButton from '@mui/lab/LoadingButton';
import TextField from '@mui/material/TextField';
import {useNavigate, useSearchParams} from 'react-router-dom';
import {useSnackbar} from 'notistack';
import * as Yup from 'yup';
import useHandleErrors from '../../../hooks/useHandleErrors';
import {
    CircularProgress,
    DialogContent,
    FormControl,
    Grid,
    InputAdornment,
    InputLabel,
    MenuItem,
    Select,
    Stack,
} from '@mui/material';

import axios from 'axios';
import SelectBank from '../../../components/SelectBank';
import {validaCNPJ, validaCPF} from '../../../global/cpfCnpj';
import ButtonVerify from './ButtonVerify';

const RegisterSchema = Yup.object().shape({
    document_type: Yup.string().required(),
    document: Yup.string()
        .required('Campo obrigatório')
        .when('document_type', {
            is: value => value === 'CPF',
            then: schema =>
                schema
                    .length(11, 'Documento inválido')
                    .test('invalid-cpf', 'Documento inválido', cpf => validaCPF(cpf)),
            otherwise: schema =>
                schema
                    .length(14, 'Documento incompleto')
                    .test('invalid-cnpj', 'Documento inválido', cnpj => validaCNPJ(cnpj)),
        }),
    holder: Yup.string().max(30, 'Máximo 30 caracteres').required('Campo obrigatório'),
    birthdate: Yup.date().required('Campo obrigatório'),
    monthly_income: Yup.number().required('Campo obrigatório'),
    professional_occupation: Yup.string().required('Campo obrigatório'),
    phone: Yup.string()
        .matches(/^\d{10,11}$/, 'Telefone inválido')
        .required('Campo obrigatório'),
    zipcode: Yup.string()
        .min(8, 'Mínimo de 8 dígitos')
        .max(8, 'Máximo de 8 dígitos')
        .required('Campo obrigatório'),
    street: Yup.string().required('Campo obrigatório'),
    complement: Yup.string().required('Campo obrigatório'),
    number: Yup.string().required('Zero caso não haja.'),
    reference_point: Yup.string().required('Campo obrigatório'),
    bank_number: Yup.string().max(3).required('Campo obrigatório'),
    agency_number: Yup.string()
        .max(4, 'Máximo 4 dígitos')
        .test('invalid-agency-number', 'Dado inválido', value => Number.isInteger(Number(value)))
        .required('Campo obrigatório'),
    agency_digit: Yup.string().nullable().max(1, 'Máximo 1 dígito'),
    account_number: Yup.string()
        .max(12, 'Máximo 13 dígitos')
        .test('invalid-account-number', 'Dado inválido', value => Number.isInteger(Number(value)))
        .required('Campo obrigatório'),
    account_digit: Yup.string()
        .max(2, 'Máximo 2 dígitos')
        .test('invalid-account-digit', 'Dado inválido', value => Number.isInteger(Number(value)))
        .required('Campo obrigatório'),
    account_type: Yup.string().required('Campo obrigatório'),
});

export default ({data, mutate, close}) => {
    const [search] = useSearchParams();
    const {verifySession, user} = useAuth();
    const {isOffline} = useApp();
    const [hasError, setError] = useState();
    useHandleErrors(hasError);
    const {enqueueSnackbar} = useSnackbar();
    const {
        values,
        errors,
        touched,
        dirty,
        handleChange,
        handleBlur,
        handleReset,
        handleSubmit,
        isSubmitting,
        setFieldValue,
        setFieldError,
        setValues,
        resetForm,
    } = useFormik({
        initialValues: {
            document_type: 'CPF',
            document: '',
            holder: '',
            birthdate: '',
            monthly_income: '',
            professional_occupation: '',
            phone: '',
            zipcode: '',
            street: '',
            complement: '',
            city: '',
            state: '',
            number: '',
            reference_point: '',
            bank_number: '',
            agency_number: '',
            agency_digit: '',
            account_number: '',
            account_digit: '',
            account_type: 'checking',
        },
        validationSchema: RegisterSchema,
        validateOnMount: true,
        onSubmit: async (values, { setSubmitting }) => {
            try {
                setSubmitting(true);
                values.zipcode = values.zipcode.toString();
                values.document = String(values.document);
                values.agency_number = values.agency_number.toString();
                values.account_number = values.account_number.toString();
                values.number = values.number.toString();
                values.phone = values.phone.toString();

                await server.put(`/backoffice/users/${values.userId}`, values);
                enqueueSnackbar('Perfil Alterado');
                setSubmitting(false);
                mutate();
            } catch (error) {
                setSubmitting(false);
                setError(error);
            }
        },
    });

    const [searchingZipCode, setSearching] = useState(false);
    const searchPostalCode = useCallback(
        async e => {
            try {
                handleBlur(e);
                setSearching(true);
                if (String(values?.zipcode).length === 8) {
                    // setFieldValue('street', '');
                    // setFieldValue('city', '');
                    // setFieldValue('state', '');

                    const {data} = await axios.get(
                        `https://viacep.com.br/ws/${values.zipcode}/json/`,
                    );

                    if (data.erro) throw 'Erro';
                    setFieldValue('street', `${data.logradouro}, ${data.bairro}`);
                    setFieldValue('city', `${data.localidade}`);
                    setFieldValue('state', `${data.uf}`);
                    setSearching(false);
                }
            } catch (error) {
                console.log('E', error);
                setFieldValue('street', '');
                setFieldValue('city', '');
                setFieldValue('state', '');
                setSearching(false);
                setFieldError('zipcode', 'CEP Inválido');
            }
        },
        [values],
    );

    useEffect(() => {
        if (data) {
            if (data.profile) {
                const profileData = { ...data.profile };
                // Converter birthdate ISO para yyyy-mm-dd
                if (profileData.birthdate && profileData.birthdate.includes('T')) {
                    profileData.birthdate = profileData.birthdate.split('T')[0];
                }
                setValues(profileData, true);
                resetForm({ values: profileData });
            }
        }
    }, [data]);

    return (
        <form onSubmit={handleSubmit}>
            <DialogContent>
                <Grid container rowGap={2} columnSpacing={2}>
                    <Grid item xs={12} justifyContent="flex-end">
                        <ButtonVerify
                            data={data}
                            mutate={mutate}
                            close={close}
                        />
                    </Grid>

                    {/* Dados Pessoais */}
                    <Grid item xs={12}>
                        <Typography variant="h6">Dados Pessoais</Typography>
                    </Grid>
                    <Grid item xs={12}>
                        <FormControl fullWidth>
                            <InputLabel id="document-type-label">Tipo do documento</InputLabel>
                            <Select
                                labelId="document-type-label"
                                id="document-type"
                                value={values.document_type}
                                label="Tipo do documento"
                                name="document_type"
                                disabled={values.approved}
                                error={Boolean(errors.document_type)}
                                helperText={errors.document_type}
                                onChange={handleChange}>
                                <MenuItem value="CPF">CPF</MenuItem>
                                <MenuItem value="CNPJ">CNPJ</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            variant="outlined"
                            fullWidth
                            required
                            label={
                                values.document_type === 'CPF'
                                    ? 'Documento (CPF)'
                                    : 'Documento (CNPJ)'
                            }
                            type="text"
                            name="document"
                            onChange={handleChange}
                            value={values.document}
                            disabled={values.approved}
                            error={Boolean(errors.document)}
                            helperText={errors.document}
                            InputProps={{
                                maxLenght: 14,
                            }}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            variant="outlined"
                            fullWidth
                            required
                            label={
                                values.document_type === 'CPF' ? 'Nome completo' : 'Razão Social'
                            }
                            type="text"
                            name="holder"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.holder}
                            disabled={values.approved}
                            error={Boolean(errors.holder)}
                            helperText={errors.holder || 'Máximo 30 caracteres'}
                            inputProps={{
                                maxLength: 30,
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            variant="outlined"
                            fullWidth
                            required
                            label="Data de Nascimento"
                            name="birthdate"
                            placeholder="dd/mm/aaaa"
                            InputLabelProps={{ shrink: true }}
                            inputProps={{ inputMode: 'numeric', maxLength: 10 }}
                            value={values.birthdate ? values.birthdate.replace(/^(\d{4})-(\d{2})-(\d{2})$/, '$3/$2/$1') : ''}
                            onChange={e => {
                                let v = e.target.value.replace(/\D/g, '');
                                if (v.length > 8) v = v.slice(0, 8);
                                if (v.length >= 5) v = v.slice(0, 2) + '/' + v.slice(2, 4) + '/' + v.slice(4);
                                else if (v.length >= 3) v = v.slice(0, 2) + '/' + v.slice(2);
                                e.target.value = v;
                                const parts = v.split('/');
                                if (parts.length === 3 && parts[2]?.length === 4) {
                                    setFieldValue('birthdate', `${parts[2]}-${parts[1]}-${parts[0]}`);
                                } else {
                                    setFieldValue('birthdate', v);
                                }
                            }}
                            onBlur={handleBlur}
                            disabled={values.approved}
                            error={Boolean(errors.birthdate)}
                            helperText={errors.birthdate}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            variant="outlined"
                            fullWidth
                            required
                            label="Renda Mensal"
                            type="number"
                            name="monthly_income"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.monthly_income}
                            disabled={values.approved}
                            error={Boolean(errors.monthly_income)}
                            helperText={errors.monthly_income}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            variant="outlined"
                            fullWidth
                            required
                            label="Ocupação Profissional"
                            type="text"
                            name="professional_occupation"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.professional_occupation}
                            disabled={values.approved}
                            error={Boolean(errors.professional_occupation)}
                            helperText={errors.professional_occupation}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            variant="outlined"
                            fullWidth
                            required
                            label="Telefone"
                            type="text"
                            name="phone"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.phone}
                            disabled={values.approved}
                            error={Boolean(errors.phone)}
                            helperText={errors.phone}
                            inputProps={{
                                maxLength: 11,
                           }}
                        />
                    </Grid>

                    {/* Endereço */}
                    <Grid item xs={12}>
                        <Typography variant="h6">Endereço</Typography>
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            variant="outlined"
                            fullWidth
                            required
                            label="CEP"
                            type="number"
                            name="zipcode"
                            disabled={values.approved}
                            onChange={handleChange}
                            onBlur={searchPostalCode}
                            value={values.zipcode}
                            error={Boolean(errors.zipcode)}
                            InputProps={{
                                maxLenght: 8,
                                endAdornment: (
                                    <InputAdornment position="end">
                                        {searchingZipCode && (
                                            <CircularProgress disableShrink={true} size={15} />
                                        )}
                                    </InputAdornment>
                                ),
                            }}
                            helperText={
                                Boolean(errors.zipcode)
                                    ? errors.zipcode
                                    : Boolean(values.street)
                                    ? `${values.street}, ${values.city}-${values.state}`
                                    : ''
                            }
                        />
                    </Grid>
                    <Grid item xs={12} md={9}>
                        <TextField
                            variant="outlined"
                            fullWidth
                            required
                            label="Complemento"
                            disabled={values.approved}
                            type="text"
                            name="complement"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.complement}
                            error={Boolean(errors.complement)}
                            helperText={errors.complement}
                        />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <TextField
                            variant="outlined"
                            fullWidth
                            required
                            disabled={values.approved}
                            label="Número"
                            type="number"
                            name="number"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.number}
                            error={Boolean(errors.number)}
                            helperText={errors.number}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            variant="outlined"
                            fullWidth
                            required
                            label="Ponto de Referência"
                            type="text"
                            name="reference_point"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.reference_point}
                            disabled={values.approved}
                            error={Boolean(errors.reference_point)}
                            helperText={errors.reference_point}
                        />
                    </Grid>

                    {/* Dados Bancários */}
                    <Grid item xs={12}>
                        <Typography variant="h6">Dados Bancários</Typography>
                   </Grid>
                    <Grid item xs={12} md={12}>
                        <SelectBank
                            name="bank_number"
                            value={values.bank_number}
                            disabled={values.approved}
                            onChange={setFieldValue}
                            error={Boolean(errors.bank_number)}
                            helperText={errors.bank_number}
                        />
                    </Grid>
                    <Grid item xs={12} md={9}>
                        <TextField
                            variant="outlined"
                            fullWidth
                            required
                            disabled={values.approved}
                            label="Agência"
                            type="text"
                            name="agency_number"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.agency_number}
                            error={Boolean(errors.agency_number)}
                            helperText={errors.agency_number}
                        />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <TextField
                            variant="outlined"
                            fullWidth
                            required
                            label="DÍgito"
                            disabled={values.approved}
                            type="text"
                            name="agency_digit"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.agency_digit}
                            error={Boolean(errors.agency_digit)}
                            helperText={errors.agency_digit}
                        />
                    </Grid>
                    <Grid item xs={12} md={9}>
                        <TextField
                            variant="outlined"
                            fullWidth
                            required
                            label="Conta"
                            type="text"
                            disabled={values.approved}
                            name="account_number"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.account_number}
                            error={Boolean(errors.account_number)}
                            helperText={errors.account_number}
                        />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <TextField
                            variant="outlined"
                            fullWidth
                            required
                            label="Dígito"
                            disabled={values.approved}
                            type="text"
                            name="account_digit"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.account_digit}
                            error={Boolean(errors.account_digit)}
                            helperText={errors.account_digit}
                        />
                    </Grid>
                    <Grid item xs={12} md={12}>
                        <FormControl fullWidth>
                            <InputLabel id="account-type-label">Tipo da conta</InputLabel>
                            <Select
                                disabled={values.approved}
                                labelId="account-type-label"
                                id="account-type"
                                value={values.account_type}
                                error={Boolean(errors.account_type)}
                                helperText={errors.account_type}
                                label="Tipo da conta"
                                name="account_type"
                                onChange={handleChange}
                            >
                                <MenuItem value="checking">Corrente</MenuItem>
                                <MenuItem value="savings">Poupança</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>

                    {/* <Grid item xs={12}>
                        <LoadingButton
                            onClick={() => navigation(-1)}
                            fullWidth
                            variant="text"
                            disabled={isSubmitting}>
                            Voltar
                        </LoadingButton>
                    </Grid> */}
                    <Grid item xs={12} sx={{ mt: 1 }}>
                        <LoadingButton
                            type="submit"
                            fullWidth
                            loading={isSubmitting}
                            variant="contained"
                            disabled={
                                !dirty ||
                                Boolean(Object.keys(errors).length) ||
                                isSubmitting ||
                                isOffline
                            }
                            sx={{
                                bgcolor: '#006a61',
                                textTransform: 'none',
                                fontWeight: 600,
                                borderRadius: '8px',
                                '&:hover': { bgcolor: '#005a52' },
                            }}
                        >
                            Salvar dados do recebedor
                        </LoadingButton>
                    </Grid>
                </Grid>
            </DialogContent>
        </form>
    );
};
